import type { UserData, LocalStorageData } from '@/types'
import { fetchUserData, saveUserData, ApiHttpError, isNetworkError } from './api'

export type LoadSource = 'remote' | 'local-offline' | 'default'
export type LoadOutcome =
  | { data: UserData; source: 'remote' }
  | { data: UserData; source: 'local-offline'; offlineSince: number }
  | { data: UserData; source: 'default' }

const LOCAL_STORAGE_KEY = 'ssdl_data'

/**
 * Get default empty user data.
 */
export function getDefaultUserData(): UserData {
  return {
    weight: 80,
    gender: 'male',
    drinks: [],
    entries: [],
  }
}

/**
 * Check if data is considered "blank" (would wipe user's history).
 * Only considers entries — weight/gender are config, not user data.
 * Encrypted data with a valid payload is never blank.
 */
export function isBlankData(data: UserData): boolean {
  if (data.encrypted && data.encryptedData) return false
  return !data.entries || data.entries.length === 0
}

/**
 * Load data from localStorage.
 */
export function loadFromLocalStorage(): LocalStorageData | null {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Failed to load from localStorage:', e)
  }
  return null
}

/**
 * Save data to localStorage.
 */
export function saveToLocalStorage(data: UserData): void {
  try {
    const storageData: LocalStorageData = {
      ...data,
      lastSync: Date.now(),
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storageData))
  } catch (e) {
    console.error('Failed to save to localStorage:', e)
  }
}

/**
 * Clear localStorage data.
 */
export function clearLocalStorage(): void {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  } catch (e) {
    console.error('Failed to clear localStorage:', e)
  }
}

/**
 * Load data — remote is authoritative.
 *
 * 1. Fetch from remote. If it succeeds (including "user has no record" → null),
 *    use whatever remote returned. A genuinely-empty remote is not overridden
 *    by stale localStorage from another device.
 * 2. Only fall back to localStorage when the network itself failed (offline).
 *    HTTP errors (4xx/5xx) propagate as errors — the caller should surface
 *    them, not silently resurrect local data.
 * 3. When falling back to local, include the cache age so the UI can warn.
 */
export async function loadData(userId: string): Promise<LoadOutcome> {
  try {
    const response = await fetchUserData(userId)

    if (response.success && response.data?.value) {
      const remoteData = response.data.value
      saveToLocalStorage(remoteData)
      return { data: remoteData, source: 'remote' }
    }

    // Remote reachable but no record → user is genuinely new.
    return { data: getDefaultUserData(), source: 'default' }
  } catch (e) {
    // HTTP errors from the server: propagate. Never silently fall back to local
    // for e.g. 500s — the caller needs to know so it can surface the error.
    if (e instanceof ApiHttpError) {
      console.error('Remote load failed (HTTP):', e.status, e.message)
      throw e
    }

    // Network error (offline / DNS / CORS). This is the one and only case
    // where localStorage is acceptable as a fallback.
    if (isNetworkError(e)) {
      const localData = loadFromLocalStorage()
      if (localData && !isBlankData(localData)) {
        const offlineSince = localData.lastSync ?? Date.now()
        console.warn('Offline: using localStorage cache from', new Date(offlineSince).toISOString())
        return { data: localData, source: 'local-offline', offlineSince }
      }
      console.error('Offline and no local cache — rethrowing')
      throw e
    }

    console.error('Unexpected load error:', e)
    throw e
  }
}

export type SaveFailureKind = 'blocked' | 'http' | 'network' | 'unknown'

export interface SaveResult {
  success: boolean
  error?: string
  kind?: SaveFailureKind
  status?: number
}

/**
 * Save data to the remote API.
 *
 * This function is remote-only. When encryption is on, the caller passes the
 * encrypted wrapper as `data` (entries: []); writing that to localStorage
 * would wipe the plaintext recovery cache, so the caller is responsible for
 * persisting plaintext to localStorage separately.
 *
 * The returned `kind` lets the caller distinguish offline from server-side
 * rejection, so the UI can tell the user what actually happened.
 */
export async function saveData(userId: string, data: UserData, existingData: UserData): Promise<SaveResult> {
  // CRITICAL: prevent blank-wipe of existing entries.
  if (isBlankData(data) && !isBlankData(existingData)) {
    console.error('BLOCKED: attempted to save blank data over existing entries')
    return { success: false, error: 'Cannot save blank data over existing data', kind: 'blocked' }
  }

  try {
    const response = await saveUserData(userId, data)
    if (!response.success) {
      console.error('Remote save rejected:', response.error)
      return { success: false, error: response.error, kind: 'http' }
    }
    return { success: true }
  } catch (e) {
    if (e instanceof ApiHttpError) {
      console.error('Remote save HTTP error:', e.status, e.message)
      return { success: false, error: e.message, kind: 'http', status: e.status }
    }
    if (isNetworkError(e)) {
      console.error('Remote save network error:', e)
      return { success: false, error: 'Network unavailable', kind: 'network' }
    }
    console.error('Remote save unexpected error:', e)
    return { success: false, error: (e as Error)?.message || 'Unknown error', kind: 'unknown' }
  }
}

/**
 * Generate a new passcode (user ID).
 * Format: XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX (32 chars + 3 dashes)
 */
export function generatePasscode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const randomBytes = new Uint8Array(32)
  crypto.getRandomValues(randomBytes)
  let result = ''
  let charCount = 0

  for (let i = 0; i < 32; i++) {
    result += chars[randomBytes[i] % chars.length]
    charCount++

    if (charCount === 8 && i < 31) {
      result += '-'
      charCount = 0
    }
  }

  return result
}

/**
 * Validate passcode format.
 * Accepts any alphanumeric string with dashes, minimum 8 characters.
 */
export function isValidPasscode(passcode: string): boolean {
  // Remove dashes and check minimum length
  const stripped = passcode.replace(/-/g, '')
  if (stripped.length < 8) return false

  // Check format (alphanumeric with dashes)
  return /^[A-Z0-9-]+$/i.test(passcode)
}
